import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", address_line1: "", address_line2: "",
    city: "", state: "", postal_code: "", country: "USA",
  });
  const [placing, setPlacing] = useState(false);
  const [order, setOrder] = useState(null);

  const shipping = subtotal >= 75 ? 0 : 4.99;
  const total = subtotal + shipping;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const field = "bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-xl px-4 py-3.5 text-white outline-none focus:border-[color:var(--cyan)] transition-colors w-full";

  const placeOrder = async (e) => {
    e.preventDefault();
    const required = ["full_name", "email", "address_line1", "city", "state", "postal_code"];
    if (required.some((k) => !form[k].trim())) { toast.error("Please complete all required fields."); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, sku: i.sku, title: i.title, price: i.price, quantity: i.quantity, image: i.image })),
        ...form,
      });
      setOrder(data);
      clear();
      window.scrollTo(0, 0);
    } catch {
      toast.error("Could not place order. Try again.");
    } finally { setPlacing(false); }
  };

  if (order) {
    return (
      <div className="bg-ink min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-5 py-20 text-center">
          <div className="h-16 w-16 rounded-full mx-auto flex items-center justify-center mb-6" style={{ background: "var(--holo)" }}>
            <CheckCircle2 className="text-black" size={32} />
          </div>
          <h1 className="h-display text-3xl text-chrome">ORDER CONFIRMED</h1>
          <p className="text-[color:var(--chrome-2)] mt-4">Order number</p>
          <p data-testid="order-number" className="text-2xl font-display font-black text-holo mt-1">{order.order_number}</p>
          <div className="glass rounded-2xl p-6 mt-8 text-left">
            <div className="flex justify-between text-sm text-[color:var(--chrome-2)]"><span>Items</span><span>${order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm text-[color:var(--chrome-2)] mt-2"><span>Shipping</span><span>{order.shipping === 0 ? "FREE" : `$${order.shipping.toFixed(2)}`}</span></div>
            <div className="flex justify-between text-white font-bold mt-3 pt-3 border-t border-[color:var(--line)]"><span>Total</span><span>${order.total.toFixed(2)}</span></div>
          </div>
          <p className="text-xs text-[color:var(--chrome-3)] mt-6 rounded-xl border border-[color:var(--line)] p-4">
            <strong className="text-[color:var(--chrome-1)]">Demo checkout:</strong> live payment processing is coming soon.
            This order was saved with status <em>awaiting payment</em>. A confirmation will be sent to {order.email}.
          </p>
          <Link to="/shop" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm mt-8">CONTINUE SHOPPING <ArrowRight size={16} /></Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-ink min-h-[60vh] flex flex-col items-center justify-center px-5 text-center">
        <h1 className="h-display text-3xl text-chrome">NOTHING TO CHECK OUT</h1>
        <Link to="/shop" className="btn-holo rounded-xl px-7 py-4 text-sm mt-8">SHOP CARDS</Link>
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen">
      <div className="mx-auto max-w-[1100px] px-5 py-12">
        <h1 className="h-display text-4xl text-chrome mb-3">CHECKOUT</h1>
        <p className="text-xs text-[color:var(--chrome-3)] flex items-center gap-1.5 mb-10"><Lock size={12} /> Secure checkout · your details are protected</p>

        <form onSubmit={placeOrder} className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <p className="eyebrow mb-5">Contact</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="co-name" className={field} placeholder="Full name *" value={form.full_name} onChange={set("full_name")} />
                <input data-testid="co-email" type="email" className={field} placeholder="Email *" value={form.email} onChange={set("email")} />
              </div>
            </div>
            <div>
              <p className="eyebrow mb-5">Shipping Address</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="co-addr1" className={`${field} sm:col-span-2`} placeholder="Address line 1 *" value={form.address_line1} onChange={set("address_line1")} />
                <input data-testid="co-addr2" className={`${field} sm:col-span-2`} placeholder="Address line 2" value={form.address_line2} onChange={set("address_line2")} />
                <input data-testid="co-city" className={field} placeholder="City *" value={form.city} onChange={set("city")} />
                <input data-testid="co-state" className={field} placeholder="State *" value={form.state} onChange={set("state")} />
                <input data-testid="co-zip" className={field} placeholder="Postal code *" value={form.postal_code} onChange={set("postal_code")} />
                <input data-testid="co-country" className={field} placeholder="Country" value={form.country} onChange={set("country")} />
              </div>
            </div>
            <div className="rounded-xl border border-[color:var(--line)] p-4 text-xs text-[color:var(--chrome-3)]">
              Payment is not processed in this demo build. Placing the order saves it for fulfillment; live payments arrive in the next phase.
            </div>
          </div>

          <div className="glass rounded-3xl p-7 h-fit lg:sticky lg:top-24">
            <p className="eyebrow mb-5">Order Summary</p>
            <div className="space-y-3 max-h-56 overflow-auto pr-1">
              {items.map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm">
                  <span className="text-[color:var(--chrome-2)] truncate mr-2">{it.title} × {it.quantity}</span>
                  <span className="text-white shrink-0">${(it.price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[color:var(--line)] mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[color:var(--chrome-2)]"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-[color:var(--chrome-2)]"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-white font-display font-bold text-lg pt-2"><span>Total</span><span>${total.toFixed(2)}</span></div>
            </div>
            <button type="submit" data-testid="place-order" disabled={placing} className="btn-holo w-full rounded-xl py-4 text-sm mt-6 disabled:opacity-60">
              {placing ? "PLACING ORDER..." : "PLACE ORDER"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
